"use client";

import { useEffect, useState } from "react";

export interface Page {
  title: string;
  body: string;
}

/**
 * The documents on the applicant's side of the table, behind a hamburger.
 *
 * FIRST ATTEMPT WAS DOCKED BESIDE THE CHAT, AND IT WAS WRONG TWICE OVER. A 416px column
 * is narrower than the engine's aligned columns, so the right-hand side of every long
 * line was sliced off — "Chesapeake Bay cross…", "DC SFRA within 30 NM ring — r…" — and
 * the panel's max-height cut the last row through the middle. On a phone it fell back to
 * a floating button pinned bottom-left, which landed underneath Next's dev-tools badge.
 *
 * A slide-over fixes both: the chat keeps the full column it was designed for, and the
 * paperwork opens at a width the tables actually fit in, scrolling in both directions
 * when they do not.
 */
export default function Paperwork({ pages }: { pages: Page[] }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);

  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open]);

  if (!pages.length) return null;
  const page = pages[sel];

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Open your paperwork"
        className="flex items-center gap-2 rounded-full bg-ground-alt hover:bg-[#ebebed] text-ink text-[13px] font-medium px-3.5 py-2 transition-colors"
      >
        <span className="flex flex-col gap-[3px]" aria-hidden>
          <span className="block w-3.5 h-[1.5px] bg-ink rounded-full" />
          <span className="block w-3.5 h-[1.5px] bg-ink rounded-full" />
          <span className="block w-3.5 h-[1.5px] bg-ink rounded-full" />
        </span>
        Paperwork
      </button>

      {open && (
        <div className="fixed inset-0 z-40 flex justify-end">
          {/* Distinct from the Close button's own label: a screen reader announcing
              "Close" twice gives no way to tell the backdrop from the control. */}
          <button
            aria-label="Dismiss paperwork"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/20 backdrop-blur-[2px]"
          />

          {/* Wide enough for the engine's widest table, and never wider than the screen. */}
          <div className="relative w-full max-w-[44rem] h-full bg-ground/95 backdrop-blur-2xl lift-lg flex flex-col">
            <div className="flex items-start justify-between gap-4 px-6 py-5">
              <div>
                <h2 className="text-[17px] font-semibold tracking-[-0.01em] text-ink">
                  Your paperwork
                </h2>
                <p className="mt-1 text-[13px] text-ink-faint">
                  What you planned and pulled for today. Quote from it freely.
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="shrink-0 rounded-full px-3 py-1.5 text-[13px] text-accent hover:bg-ground-alt transition-colors"
              >
                Close
              </button>
            </div>

            {/* A segmented control, as on any Apple settings pane: one recessed track,
                the selection a raised white tile. Seven tabs do not fit one row on a
                phone, so the track wraps rather than scrolling out of reach. */}
            <div className="px-6 pb-4">
              <div className="segmented flex-wrap">
                {pages.map((p, i) => (
                  <button
                    key={p.title}
                    onClick={() => setSel(i)}
                    className={`segment ${i === sel ? "segment-on" : ""}`}
                  >
                    {p.title}
                  </button>
                ))}
              </div>
            </div>

            {/* `pre-wrap`, not `pre`. The engine lays these out in fixed columns and the
                short rows still line up — but a few lines are prose, like the SFRA entry
                that runs to "mandatory squawk 1226, radio contact, flight plan required".
                Under `pre` that one needed a sideways scroll nobody would discover, which
                is clipping with extra steps. Let the long ones wrap; the tables are
                unaffected because their rows are short. */}
            <div className="flex-1 overflow-y-auto">
              <pre className="px-6 py-5 text-[12.5px] leading-[1.65] text-ink-soft whitespace-pre-wrap break-words font-mono">
                {page.body}
              </pre>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
